import type { UseFormReturn } from 'react-hook-form';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Client } from '~/api/clients/types';

import { useClients } from '~/api/clients';
import { Button, Card, Dialog } from '~/components';
import { Field, Input, Radio, Select } from '~/components/Form';
import { useIntl } from '~/hooks/useIntl';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { formatVatNumber } from '~/utils/string';

import { ClientTypeIcon } from '../ClientTypeIcon/ClientTypeIcon';
import { useClientContactSelection } from '../useClientContactSelection';
import { useNavigateWithRedirect } from '../useNavigateWithRedirect';
import {
  actionsStyle,
  bodyStyle,
  contactCardsRowStyle,
  contactsStyle,
  dialogStyle,
  emptyStyle,
  headerStyle,
  legendStyle,
  listStyle,
  rowButtonStyle,
  rowNameStyle,
  rowSubStyle,
  rowTextStyle,
  searchStyle,
  selectedCardStyle,
  selectedHeaderStyle,
  toolbarStyle,
} from './ClientPickerDialog.css';

type FormWithClientContact = {
  clientId: number | null;
  condition: { id: number; type: 'condition' | 'customAgreement' | 'customCondition' } | null;
  contactId: number | null;
  specialConditions: string;
};

type TypeFilter = 'all' | Client['clientType'];

type Props<T extends FormWithClientContact> = {
  form: T extends FormWithClientContact ? UseFormReturn<T> : never;
  onClose: () => void;
};

export const ClientPickerDialog = <T extends FormWithClientContact>({ form, onClose }: Props<T>) => {
  return (
    <Dialog.Body className={dialogStyle}>
      <ClientPickerDialogContent form={form} onClose={onClose} />
    </Dialog.Body>
  );
};

const ClientPickerDialogContent = <T extends FormWithClientContact>({ form, onClose }: Props<T>) => {
  const { data: clients } = useClients();
  const { compareForSort } = useIntl();
  const { t } = useTranslation(['clients', 'invoices']);

  const navigateWithRedirect = useNavigateWithRedirect(form);
  const { selectClientContact } = useClientContactSelection(form);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [stagedClientId, setStagedClientId] = useState<number | null>(null);
  const [stagedContactId, setStagedContactId] = useState<number | null>(null);

  const stagedClient = useMemo(() => clients.find(({ id }) => id === stagedClientId) ?? null, [clients, stagedClientId]);
  const stagedContacts = useMemo(() => (stagedClient ? stagedClient.contacts.filter(({ deleted }) => !deleted) : []), [stagedClient]);

  const filteredClients = useMemo(() => {
    const query = search.trim().toLowerCase();

    return clients
      .filter((client) => typeFilter === 'all' || client.clientType === typeFilter)
      .filter((client) => {
        if (!query) return true;

        const haystack = [client.name, ...client.contacts.filter(({ deleted }) => !deleted).map(({ fullName }) => fullName)];
        return haystack.some((value) => value.toLowerCase().includes(query));
      })
      .sort((a, b) => compareForSort(a.name, b.name));
  }, [clients, compareForSort, search, typeFilter]);

  const typeItems = useMemo(
    () =>
      (['all', 'organisation', 'private'] satisfies TypeFilter[]).map((value) => ({
        label: t(`invoices:createEdit.clientModal.typeFilter.${value}`),
        value,
      })),
    [t],
  );

  const selectClient = (client: Client) => {
    setStagedClientId(client.id);

    // Auto-stage the contact when there's exactly one to choose from — saves a click.
    const contacts = client.contacts.filter(({ deleted }) => !deleted);
    setStagedContactId(contacts.length === 1 ? contacts[0].id : null);
  };

  const confirm = () => {
    const stagedContact = stagedContacts.find(({ id }) => id === stagedContactId);
    if (!stagedClient || !stagedContact) return;

    selectClientContact(stagedClient, stagedContact);
    onClose();
  };

  const canConfirm = stagedClient !== null && stagedContactId !== null;

  return (
    <>
      <Dialog.Header className={headerStyle} hideCloseButton title={t('invoices:createEdit.clientModal.title')}>
        <Button emphasis="secondary" icon="Add" onClick={() => navigateWithRedirect(routes.createClient)}>
          {t('invoices:createEdit.clientModal.newClient')}
        </Button>
      </Dialog.Header>

      <div className={bodyStyle}>
        <div className={toolbarStyle}>
          <Field.Root className={searchStyle}>
            <Input
              autoFocus
              clearable
              icon="Search"
              onValueChange={(value) => setSearch(value)}
              placeholder={t('invoices:createEdit.clientModal.searchPlaceholder')}
              type="search"
              value={search}
            />
          </Field.Root>

          <Field.Root>
            <Select.Root items={typeItems} onValueChange={(value) => setTypeFilter(value ?? 'all')} value={typeFilter}>
              <Select.Button placeholder={t('invoices:createEdit.clientModal.typeFilter.all')} />

              <Select.List>
                {typeItems.map(({ label, value }) => (
                  <Select.Item key={value} value={value}>
                    {label}
                  </Select.Item>
                ))}
              </Select.List>
            </Select.Root>
          </Field.Root>
        </div>

        <div className={listStyle}>
          {filteredClients.length === 0 && <p className={emptyStyle}>{t('invoices:createEdit.clientModal.emptyList')}</p>}

          {filteredClients.map((client) => {
            const identifier = client.vatNumber ? formatVatNumber(client.vatNumber) : (client.otherNumber ?? '');
            const isSelected = stagedClientId === client.id;

            const header = (
              <>
                <ClientTypeIcon clientType={client.clientType} />

                <span className={rowTextStyle}>
                  <span className={rowNameStyle}>{client.name}</span>

                  {identifier && <span className={rowSubStyle}>{identifier}</span>}
                </span>
              </>
            );

            if (!isSelected) {
              return (
                <Card.Root
                  className={rowButtonStyle}
                  key={client.id}
                  onClick={() => selectClient(client)}
                  render={<button type="button" />}
                  variant="primary-light"
                >
                  {header}
                </Card.Root>
              );
            }

            return (
              <Card.Root className={selectedCardStyle} key={client.id} size="small" variant="primary-accent">
                <div className={selectedHeaderStyle}>{header}</div>

                {client.clientType === 'organisation' && (
                  <div className={contactsStyle}>
                    <Field.Root name="contactId">
                      <Radio.Group onValueChange={(value) => setStagedContactId(value)} value={stagedContactId}>
                        <Radio.GroupLegend className={legendStyle}>{t('invoices:createEdit.clientModal.contactsLegend')}</Radio.GroupLegend>

                        <div className={contactCardsRowStyle}>
                          {stagedContacts.map((contact) => (
                            <Radio.Root key={contact.id} value={contact.id}>
                              <Radio.Label>{contact.fullName}</Radio.Label>

                              <Radio.Description>{contact.emails[0]?.email ?? ''}</Radio.Description>
                            </Radio.Root>
                          ))}
                        </div>
                      </Radio.Group>
                    </Field.Root>

                    <Button
                      emphasis="tertiary"
                      icon="Add"
                      onClick={() => navigateWithRedirect(r(routes.createContact, { clientId: client.id }))}
                    >
                      {t('invoices:createEdit.clientModal.addContact')}
                    </Button>
                  </div>
                )}
              </Card.Root>
            );
          })}
        </div>
      </div>

      <Dialog.Actions className={actionsStyle}>
        <Dialog.Close render={<Button emphasis="secondary">{t('invoices:createEdit.clientModal.cancel')}</Button>} />

        <Button disabled={!canConfirm} onClick={confirm}>
          {t('invoices:createEdit.clientModal.confirm')}
        </Button>
      </Dialog.Actions>
    </>
  );
};
